import CloseSvg from "@/src/assets/icons/icon-close-red.svg";
import PencelSvg from "@/src/assets/icons/icon-edit-pencel.svg";
import { Button, IconButton } from "@/src/components/atom/Button";
import Dialog from "@/src/components/atom/Dialog";
import DialogFooterContainer from "@/src/components/atom/Dialog/DialogFooterContainer";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import SingleImageUpload from "@/src/components/molecule/SingleImageUpload";
import useAlert from "@/src/hooks/useAlert";
import { useConfirmImporterPoMutation, useConfirmImporterScMutation } from "@/src/store/apis/contracts/contractDetail";
import { useAddSignMutation } from "@/src/store/apis/corporate/company";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface RegisterSignDialogProps {
  id: number;
  onClose: () => void;
  signatureType?: "PO" | "SC";
  onSuccess?: () => void;
}

const RegisterSignDialog = ({ id, onClose, signatureType = "SC", onSuccess }: RegisterSignDialogProps) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const [sign, setSign] = useState<{
    id: number;
    mediaUrl: string;
  }>();

  const [addSign, { isLoading: isAddingSign }] = useAddSignMutation();
  const [confirmImporterSc, { isLoading: isConfirmingSc }] =
    useConfirmImporterScMutation();
  const [confirmImporterPo, { isLoading: isConfirmingPo }] =
    useConfirmImporterPoMutation();

  const handleRegister = async () => {
    if (!sign) return;
    try {
      await addSign({ signatureMediaId: sign.id }).unwrap();
      
      // 서명 타입에 따라 적절한 API 호출
      if (signatureType === "PO") {
        await confirmImporterPo({
          id,
          signatureMediaId: sign.id,
        }).unwrap();
      } else {
        await confirmImporterSc({
          id,
          signatureMediaId: sign.id,
        }).unwrap();
      }
      
      // onSuccess 콜백 호출
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (e: any) {
      const message = Array.isArray(e.data.message)
        ? e.data.message[0]
        : e.data.message;
      alert.showAlert({ type: "error", message });
    }
  };

  return (
    <Dialog
      title={t("contract:importer.detail.dialogTitle.registerSignature")}
      open
      onOpenChange={onClose}
      width={500}
      footer={
        <DialogFooterContainer>
          <Button 
            buttonColor="black" 
            buttonGrade="tertiary" 
            onClick={onClose}
            style={{ width: 220 }}
          >
            <Typo typoType="btn3m" color="gray2">
              {t("common:cancel")}
            </Typo>
          </Button>
          <StyledButton
            isLoading={isAddingSign || isConfirmingSc || isConfirmingPo}
            disabled={!sign}
            onClick={handleRegister}
            buttonColor="blue"
            buttonGrade="primary"
            style={{ width: 220 }}
          >
            <Typo typoType="btn3m" color="white">
              등록하기
            </Typo>
          </StyledButton>
        </DialogFooterContainer>
      }
    >
      <Container>
        <SingleImageUpload
          onChange={(media) =>
            setSign({
              id: media.id,
              mediaUrl: media.mediaUrl,
            })
          }
          value={sign ? sign.mediaUrl : undefined}
          topAccessory={sign ? ({ handleEdit, handleRemove }) => {
            return (
              <IconButtonContainer>
                <StyledIconButton
                  buttonColor="black"
                  buttonGrade="tertiary"
                  buttonSize={24}
                  onClick={() => handleEdit()}
                >
                  <Icon iconSrc={PencelSvg} iconSize={16} />
                </StyledIconButton>
                <StyledIconButton
                  buttonColor="black"
                  buttonGrade="tertiary"
                  buttonSize={24}
                  onClick={() => {
                    handleRemove();
                    setSign(undefined);
                  }}
                >
                  <Icon iconSrc={CloseSvg} iconSize={16} />
                </StyledIconButton>
              </IconButtonContainer>
            );
          } : undefined}
        />
      </Container>
    </Dialog>
  );
};

export default RegisterSignDialog;

const IconButtonContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
`;

const StyledIconButton = styled(IconButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
`;

const Container = styled.div`
  width: 100%;
  height: 205px;
`;

const StyledButton = styled(Button)<{ disabled?: boolean }>`
  ${({ disabled }) => disabled && `
    background-color: #aeb7bc !important;
    border-color: #aeb7bc !important;
    
    &:hover {
      background-color: #aeb7bc !important;
      border-color: #aeb7bc !important;
    }
  `}
  
  ${({ disabled }) => !disabled && `
    background-color: #1c3d61 !important;
    border-color: #1c3d61 !important;
    
    &:hover {
      background-color: #1c3d61 !important;
      border-color: #1c3d61 !important;
    }
  `}
`;
